/**
 * Review Component
 *
 * Individual review card with Figma design support for two variants:
 * 1. Review with text and optional contractor response
 * 2. Review without text (shows "No description")
 */

'use client';

import { useTranslations, useLocale } from 'next-intl';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { Avatar } from '@/src/shared/ui/avatars/Avatar';
import { Stars } from '@/src/shared/ui/rating-stars/Stars';
import type { ProfileReview } from '../types';

export interface ReviewProps {
  review: ProfileReview;
  contractorName?: string;
}

/**
 * Formats date to "MMM DD, YYYY" format (e.g., "Aug 12, 2025" or "12 août 2025")
 */
function formatDate(date: Date, locale: string): string {
  return new Date(date).toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

export function Review({ review, contractorName }: ReviewProps) {
  const t = useTranslations('profile.reviews');
  const locale = useLocale();

  return (
    <div className="border-t border-accent-secondary py-2.5">
      <div className="space-y-5">
        {/* Header Row: Avatar, Name/Date, Stars */}
        <div className="flex items-start justify-between gap-4">
          {/* Avatar and reviewer info */}
          <div className="flex items-center gap-3">
            <Avatar
              src={review.reviewer.avatarUrl || undefined}
              alt={review.reviewer.name}
              size="sm"
              fallback={review.reviewer.name.charAt(0)}
            />
            <div className="flex flex-col">
              <Typography variant="body" weight="medium" color="primary">
                {review.reviewer.name}
              </Typography>
              <Typography variant="bodySm" color="tertiary">
                {formatDate(review.createdAt, locale)}
              </Typography>
            </div>
          </div>

          {/* Stars rating */}
          <Stars
            count={5}
            rating={review.rating}
            size="md"
            gap="gap-0.5"
            color="text-accent-secondary"
            emptyColor="text-border-secondary"
          />
        </div>

        {/* Review Comment or "No description" */}
        {review.comment ? (
          <Typography variant="bodySm" color="primary" className="leading-5">
            {review.comment}
          </Typography>
        ) : (
          <Typography variant="bodySm" color="tertiary">
            {t('noDescription')}
          </Typography>
        )}

        {/* Contractor Response (if exists) */}
        {review.response && (
          <div className="bg-background-3 rounded-lg p-2.5 space-y-1">
            <Typography variant="bodySm" color="tertiary">
              {contractorName ? t('contractorReply', { name: contractorName }) : t('reply')}
            </Typography>
            <Typography variant="bodySm" color="primary" className="leading-5">
              {review.response.content}
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
}
